import { EMPTY_ARRAY } from "@regrello/core-utils";
import { FeatureFlagKey } from "@regrello/feature-flags-api";
import type { FieldInstanceFields } from "@regrello/graphql-api";
import { type RegrelloDataGridV2EditableCellProps, useDataGridV2EditableCell } from "@regrello/ui-core";

import { FeatureFlagService } from "../../../../../../services/FeatureFlagService";
import { isItemsEqual, itemPredicate } from "../../../../customFields/plugins/MultiSelectFieldPlugin";
import {
  extractAllowedValuesFromFieldForFrontend,
  extractAllowedValuesFromFieldVersionForFrontend,
} from "../../../../customFields/plugins/utils/extractAllowedValuesForFrontend";
import { RegrelloFormFieldMultiSelect } from "../../../../formFields/RegrelloFormFieldMultiSelect";
import type { MultiSelectPluginFrontendValue } from "./types";

export interface RegrelloEditableCellMultiSelectProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: MultiSelectPluginFrontendValue | null;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: MultiSelectPluginFrontendValue | null) => void;
}

/**
 * An editable cell in which the user can select multiple items from a list. Intended for use in
 * `RegrelloDataGridV2`.
 */
export function RegrelloEditableCellMultiSelect({
  api,
  eventKey,
  fieldInstance,
  value,
  onValueChange,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellMultiSelectProps>) {
  const isAllowedValuesVersioningEnabled = FeatureFlagService.isEnabled(FeatureFlagKey.SPECTRUM_ALLOWED_VALUES_2024_12);

  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersion is unexpectedly not defined");
  }

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({
    api,
    eventKey,
    // These keys are used to cycle through menu items:
    suppressKeyArrowDown: true,
    suppressKeyArrowUp: true,
  });

  return (
    <div style={{ width: columnWidth }}>
      <RegrelloFormFieldMultiSelect
        autoFocus={true}
        getSelectedItemProps={(item) => {
          return {
            children: item.value,
          };
        }}
        inputProps={{ onKeyDownCapture }}
        interactionMode="editableTableCell"
        isItemsEqual={isItemsEqual}
        itemPredicate={itemPredicate}
        items={
          isAllowedValuesVersioningEnabled
            ? extractAllowedValuesFromFieldVersionForFrontend(fieldInstance.spectrumFieldVersion)
            : extractAllowedValuesFromFieldForFrontend(fieldInstance.field)
        }
        onBlur={() => {
          api.stopEditing();
        }}
        onChange={onValueChange}
        renderItem={(item) => {
          return {
            key: item.id,
            text: item.value,
          };
        }}
        size="x-small"
        value={value ?? EMPTY_ARRAY}
      />
    </div>
  );
}
